/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import { types, applySnapshot, getSnapshot, flow } from 'mobx-state-tree';
import {
  ADVANCED_SETTINGS_AUDIO_FETCH_REASON,
  ENABLE_AGENT_THINKING_MSG_KEY,
  ENABLE_MEMORY_SAVER_KEY
} from '../../constants/StudioTextConstants';
import fetchTenantAudioProviders, {
  getModelsForProvider,
  getUniqueProviders,
  getVoicesForProviderModel
} from '../../hooks/fetchAudioOptionsConfig';
import {
  buildMergedOtherParams as buildMergedOtherParamsFromState,
  getAdditionalParamsJsonText,
  getAudioOptionsFromParams,
  isAudioSelectionComplete
} from '../../utils/advancedSettingsUtils';
import {
  getGraphAgentThinkingFromParams,
  getGraphMemorySaverFromParams,
  getTenantAgentThinkingEnabled,
  getTenantMemorySaverEnabled,
  resolveAgentThinkingUiState,
  resolveMemorySaverUiState
} from '../../utils/featureConfigUtils';

const savedAudioShape = types.frozen();

export const defaultAdvancedSettingsState = {
  jsonText: '{}',
  jsonError: null,
  jsonExpanded: false,
  enableMemorySaver: false,
  enableAgentThinkingMsg: false,
  audioEnabled: false,
  selectedProviderId: '',
  selectedModel: '',
  selectedVoiceName: '',
  savedAudio: null,
  pendingProviderRestore: false,
  pendingModelRestore: false,
  audioProviderConfig: [],
  audioConfigLoading: false
};

const AdvancedSettingsStateModel = types
  .model('AdvancedSettingsStateModel', {
    jsonText: types.optional(types.string, '{}'),
    jsonError: types.maybeNull(types.string),
    jsonExpanded: types.optional(types.boolean, false),
    enableMemorySaver: types.optional(types.boolean, false),
    enableAgentThinkingMsg: types.optional(types.boolean, false),
    audioEnabled: types.optional(types.boolean, false),
    selectedProviderId: types.optional(types.string, ''),
    selectedModel: types.optional(types.string, ''),
    selectedVoiceName: types.optional(types.string, ''),
    savedAudio: types.maybeNull(savedAudioShape),
    pendingProviderRestore: types.optional(types.boolean, false),
    pendingModelRestore: types.optional(types.boolean, false),
    audioProviderConfig: types.optional(types.array(types.frozen()), []),
    audioConfigLoading: types.optional(types.boolean, false)
  })
  .views((self) => ({
    get audioProviders() {
      return getUniqueProviders(self.audioProviderConfig);
    },
    get availableModels() {
      return getModelsForProvider(self.audioProviderConfig, self.selectedProviderId);
    },
    get availableVoices() {
      return getVoicesForProviderModel(self.audioProviderConfig, self.selectedProviderId, self.selectedModel);
    },
    get audioSelectionComplete() {
      return isAudioSelectionComplete(self.selectedProviderId, self.selectedModel, self.selectedVoiceName);
    },
    get audioBlocksSave() {
      return self.audioEnabled && !self.audioSelectionComplete;
    }
  }))
  .actions((self) => {
    const updateAdvancedSettingsState = (partial) => {
      applySnapshot(self, {
        ...getSnapshot(self),
        ...partial
      });
    };

    return {
      reset() {
        applySnapshot(self, defaultAdvancedSettingsState);
      },

      initFromOtherParams(
        otherParams,
        tenantMemorySaverEnabled = getTenantMemorySaverEnabled(),
        tenantAgentThinkingEnabled = getTenantAgentThinkingEnabled()
      ) {
        const params = otherParams || {};
        const saved = getAudioOptionsFromParams(params);
        const { checked: memorySaverChecked } = resolveMemorySaverUiState(
          tenantMemorySaverEnabled,
          getGraphMemorySaverFromParams(params)
        );
        const { checked: agentThinkingChecked } = resolveAgentThinkingUiState(
          tenantAgentThinkingEnabled,
          getGraphAgentThinkingFromParams(params)
        );

        updateAdvancedSettingsState({
          enableMemorySaver: memorySaverChecked,
          enableAgentThinkingMsg: agentThinkingChecked,
          savedAudio: saved,
          pendingProviderRestore: Boolean(saved.providerId),
          pendingModelRestore: Boolean(saved.audioModelName),
          audioEnabled: saved.audioEnabled,
          selectedProviderId: '',
          selectedModel: '',
          selectedVoiceName: '',
          jsonText: getAdditionalParamsJsonText(params),
          jsonError: null
        });
      },

      setJsonText(value) {
        updateAdvancedSettingsState({ jsonText: value });
      },

      setJsonError(error) {
        updateAdvancedSettingsState({ jsonError: error });
      },

      setJsonExpanded(value) {
        updateAdvancedSettingsState({ jsonExpanded: value });
      },

      setEnableMemorySaver(checked) {
        const patch = { enableMemorySaver: checked };
        try {
          const parsed = JSON.parse(self.jsonText);
          if (Object.prototype.hasOwnProperty.call(parsed, ENABLE_MEMORY_SAVER_KEY)) {
            parsed[ENABLE_MEMORY_SAVER_KEY] = checked;
            patch.jsonText = JSON.stringify(parsed, null, 2);
          }
        } catch {
          // JSON invalid — toggle still updates
        }
        updateAdvancedSettingsState(patch);
      },

      setEnableAgentThinkingMsg(checked) {
        const patch = { enableAgentThinkingMsg: checked };
        try {
          const parsed = JSON.parse(self.jsonText);
          if (Object.prototype.hasOwnProperty.call(parsed, ENABLE_AGENT_THINKING_MSG_KEY)) {
            parsed[ENABLE_AGENT_THINKING_MSG_KEY] = checked;
            patch.jsonText = JSON.stringify(parsed, null, 2);
          }
        } catch {
          // JSON invalid — toggle still updates
        }
        updateAdvancedSettingsState(patch);
      },

      syncMemorySaverFromJson(tenantFeatureEnabled = false) {
        try {
          const parsed = JSON.parse(self.jsonText);
          const graphMemorySaver = getGraphMemorySaverFromParams(parsed);
          if (graphMemorySaver === undefined) {
            return;
          }
          const { checked } = resolveMemorySaverUiState(tenantFeatureEnabled, graphMemorySaver);
          updateAdvancedSettingsState({ enableMemorySaver: checked });
        } catch {
          // Keep current toggle state while JSON is invalid
        }
      },

      syncAgentThinkingFromJson(tenantFeatureEnabled = false) {
        try {
          const parsed = JSON.parse(self.jsonText);
          const graphAgentThinking = getGraphAgentThinkingFromParams(parsed);
          if (graphAgentThinking === undefined) {
            return;
          }
          const { checked } = resolveAgentThinkingUiState(tenantFeatureEnabled, graphAgentThinking);
          updateAdvancedSettingsState({ enableAgentThinkingMsg: checked });
        } catch {
          // Keep current toggle state while JSON is invalid
        }
      },

      setAudioEnabled(checked) {
        updateAdvancedSettingsState({ audioEnabled: checked });
      },

      setSelectedProviderId(providerId) {
        updateAdvancedSettingsState({
          savedAudio: null,
          pendingModelRestore: false,
          selectedProviderId: providerId
        });
        self.syncModelAfterProviderChange();
      },

      setSelectedModel(model) {
        updateAdvancedSettingsState({
          savedAudio: null,
          selectedModel: model
        });
        self.syncVoiceAfterModelChange();
      },

      setSelectedVoiceName(voiceName) {
        updateAdvancedSettingsState({ selectedVoiceName: voiceName });
      },

      syncProviderRestoreAfterLoad() {
        if (!self.pendingProviderRestore || !self.audioProviders.length) {
          return;
        }

        const savedProviderId = self.savedAudio?.providerId;
        if (savedProviderId && self.audioProviders.some((item) => item.providerId === savedProviderId)) {
          updateAdvancedSettingsState({ selectedProviderId: savedProviderId });
          self.syncModelAfterProviderChange();
        }
        updateAdvancedSettingsState({ pendingProviderRestore: false });
      },

      syncModelAfterProviderChange() {
        if (!self.selectedProviderId || !self.audioProviderConfig.length) {
          if (!self.selectedProviderId) {
            updateAdvancedSettingsState({ selectedModel: '', selectedVoiceName: '' });
          }
          return;
        }

        const pendingModelRestore =
          self.pendingModelRestore && self.savedAudio?.providerId === self.selectedProviderId
            ? self.savedAudio.audioModelName
            : '';

        if (pendingModelRestore) {
          if (self.availableModels.some((item) => item.model === pendingModelRestore)) {
            updateAdvancedSettingsState({ selectedModel: pendingModelRestore, pendingModelRestore: false });
            self.syncVoiceAfterModelChange();
            return;
          }
          updateAdvancedSettingsState({ pendingModelRestore: false });
        }

        updateAdvancedSettingsState({ selectedModel: '', selectedVoiceName: '' });
      },

      syncVoiceAfterModelChange() {
        if (!self.selectedProviderId || !self.selectedModel || !self.audioProviderConfig.length) {
          if (!self.selectedModel) {
            updateAdvancedSettingsState({ selectedVoiceName: '' });
          }
          return;
        }

        const pendingVoiceRestore =
          self.savedAudio?.providerId === self.selectedProviderId &&
          self.savedAudio?.audioModelName === self.selectedModel
            ? self.savedAudio.voiceName
            : '';

        if (pendingVoiceRestore) {
          if (self.availableVoices.some((item) => item.name === pendingVoiceRestore)) {
            updateAdvancedSettingsState({ selectedVoiceName: pendingVoiceRestore });
          }
          return;
        }

        updateAdvancedSettingsState({ selectedVoiceName: '' });
      },

      clearAudioProviderConfig() {
        updateAdvancedSettingsState({ audioProviderConfig: [], audioConfigLoading: false });
      },

      fetchAudioProviders: flow(function* fetchAudioProviders(tenantId) {
        updateAdvancedSettingsState({ audioConfigLoading: true, audioProviderConfig: [] });

        if (!tenantId) {
          updateAdvancedSettingsState({ audioConfigLoading: false });
          return { success: false, reason: ADVANCED_SETTINGS_AUDIO_FETCH_REASON.MISSING_TENANT };
        }

        try {
          const config = yield fetchTenantAudioProviders(tenantId);
          updateAdvancedSettingsState({
            audioProviderConfig: Array.isArray(config) ? config : [],
            audioConfigLoading: false
          });
          self.syncProviderRestoreAfterLoad();
          return { success: true };
        } catch {
          updateAdvancedSettingsState({ audioProviderConfig: [], audioConfigLoading: false });
          return { success: false, reason: ADVANCED_SETTINGS_AUDIO_FETCH_REASON.FETCH_ERROR };
        }
      }),

      getMergedOtherParamsForSave(tenantMemorySaverEnabled = false, tenantAgentThinkingEnabled = false) {
        return buildMergedOtherParamsFromState(
          getSnapshot(self),
          self.audioProviderConfig.slice(),
          tenantMemorySaverEnabled,
          tenantAgentThinkingEnabled
        );
      }
    };
  });

export default AdvancedSettingsStateModel;
